import React, { useEffect, useMemo, useState } from 'react';
import { Badge } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { addMonths, format, isAfter, lastDayOfMonth, startOfMonth, sub } from 'date-fns';
import { DateFilter } from './components';
import TableComponent from '../commons/components/TableComponent';
import './assets/styles/CompanyKPIContainer.scss';
import IconArrowGround from './assets/images/icons/arrow-ground.svg';
import { downloadTableAsExcel, getDateFromString, isPartialMatch } from '../../utils/helpers';
import { filterType } from '../../constants/filterOptions';
import { getAudiobooksAllocationInfo } from './store/actions';

const AudioBooksAllocationContainer = () => {
  const DEFAULT_RANGE_MONTHS = 3;
  const now = new Date();
  const startDateDate = startOfMonth(addMonths(new Date(), -DEFAULT_RANGE_MONTHS));
  const endDateDate = sub(now, { days: 1 });
  const [startValue, setStartValue] = useState(startDateDate);
  const [endValue, setEndValue] = useState(endDateDate);
  const audiobooksAllocation = useSelector((state) => state.kpi.audiobooksAllocation);
  const [showDateFilter, setShowDateFilter] = useState(false);
  const [currentView, setCurrentView] = useState(filterType.custom);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  
  useEffect(() => {
    if (startValue && endValue) {
      dispatch(getAudiobooksAllocationInfo({
        startDate: format(startValue, 'yyyy-MM-dd'),
        endDate: format(endValue, 'yyyy-MM-dd'),
      }));
    }
  }, [startValue, endValue]);

  function getMonthsInRange(startDate, endDate) {
    const months = [];
    let currentDate = new Date(startDate);
    let currentEndDate = new Date(endDate);
    while (
      isAfter(currentEndDate, currentDate) ||
      currentDate.getMonth() === currentEndDate.getMonth()
    ) {
      months.push(format(lastDayOfMonth(currentDate), 'yyyy-MM-dd'));
      currentDate = addMonths(currentDate, 1);
    }
    return months;
  }

  const getHeaderSubtitle = (month) => {
    const sDate = getDateFromString(startValue);
    const eDate = getDateFromString(endValue);

    if (startValue && isPartialMatch(month, startValue) && sDate.getDate() > 1) {
      return `${format(sDate, 'do')} - ${format(lastDayOfMonth(sDate), 'do MMM yyyy')}`;
    } else if (
      endValue &&
      isPartialMatch(month, endValue) &&
      eDate.getDate() < 31 &&
      eDate.getMonth() !== sDate.getMonth()
    ) {
      return `${format(eDate, 'do MMM yyyy')}`;
    }
    return;
  };

  const monthsInRange = getMonthsInRange(startValue, endValue);

  const columns = useMemo(() => [
    {
      Header: 'Content Name',
      accessor: 'contentName',
      clickable: true,
    },
    {
      Header: 'Vendor Name',
      accessor: 'vendorName',
    },
    {
      Header: 'v_key_1',
      accessor: 'v_key1',
      disableSortBy: true,
    },
    {
      Header: 'v_value_1',
      accessor: 'v_value1',
      disableSortBy: true,
    },
    ...monthsInRange.map((month, index) => ({
      Header: format(new Date(month), 'MMMM'),
      accessor: month,
    })),
  ], [audiobooksAllocation.loading, monthsInRange]);

  const data = useMemo(() => {
    if (Object.keys(audiobooksAllocation.data || {}).length === 0) {
      const placeholderRows = Array.from({ length: 20 }, (_, index) => ({
        contentName: '',
        vendorName: '',
        v_key1: '',
        v_value1: '',
        ...monthsInRange.reduce((acc, month) => {
          acc[month] = '-';
          return acc;
        }, {}),
      }));
      return placeholderRows;
    }
    return  Object.keys(audiobooksAllocation.data || {}).map(key => {
      const data = {
        contentName: key,
        vendorName: audiobooksAllocation.data[key].vendor_name,
        v_key1: audiobooksAllocation.data[key].v_key1,
        v_value1: audiobooksAllocation.data[key].v_value1,
      }
      monthsInRange.forEach(month => {
        const matchedMonth = Object.keys(audiobooksAllocation.data[key].months)?.find(m => {
          if (isPartialMatch(m, month)) {
            return true;
          }
          return false;
        });
        if (matchedMonth) {
          data[month] = audiobooksAllocation.data[key].months[matchedMonth].audio_books_allocated;
        } else {
          data[month] = 0;
        }
      });

      return data;
    });
  }, [audiobooksAllocation, monthsInRange]);

  function downloadSheet() {
    const tableElement = document.getElementById('data-table-container');
    const rangeText = `${format(getDateFromString(startValue), 'yyyy-MM-dd')} to ${format(
      getDateFromString(endValue),
      'yyyy-MM-dd'
    )}`;
    downloadTableAsExcel(tableElement, `audiobooks-allocation-info(${rangeText}).xlsx`);
  }

  const onClickDateFilter = (e) => {
    e.stopPropagation();
    setShowDateFilter(!showDateFilter);
  };

  const onFilterUpdate = (selectedFilter, sDate, eDate) => {
    setStartValue(sDate);
    setEndValue(eDate);
    setCurrentView(selectedFilter.value);
  };

  const onClick = (cell) => {
    if (cell.column.id === 'contentName') {
      navigate(`/audio-books/${cell.value}`, {
        state: {
          startDate: format(startValue, 'yyyy-MM-dd'),
          endDate: format(endValue, 'yyyy-MM-dd'),
        }
      });
    }
  }

  return (
    <div className='container-fluid kpi'>
      <div className='company-report--actions'>
        <div className='d-flex align-items-center gap-3'>
          <div className='company-report--actions--date'>
            <div className='company-report--actions--date--wrapper' onClick={onClickDateFilter}>
              <Badge className='current-filter' bg='light' text='dark'>
                {currentView || 'Custom'}
              </Badge>
              {startValue && endValue && (
                <span className='date-range'>
                  {format(getDateFromString(startValue), 'MMM d')} -{' '}
                  {format(getDateFromString(endValue), 'MMM d, yyyy')}
                </span>
              )}
              <span className='select-icon-down' aria-hidden='true'></span>
            </div>
            {showDateFilter && (
              <DateFilter
                onClose={() => setShowDateFilter(false)}
                startValue={startValue}
                endValue={endValue}
                onFilterUpdate={onFilterUpdate}
              />
            )}
          </div>
          <div className={`company-report--actions--download`} onClick={() => downloadSheet()}>
            <img src={IconArrowGround} alt='' />
            <span>Download</span>
          </div>
        </div>
      </div>
      <div id='data-table-container' className='data-table-container table-responsive mt-3'>
        <TableComponent columns={columns} data={data} onClick={onClick} />
      </div>
    </div>
  );
};

export default AudioBooksAllocationContainer;
